/**
 * Odkud poptávka přišla – pro offline konverze (poptávka → obhlídka → zakázka).
 *
 * Při vstupu na web zachytíme parametry reklamy (gclid, gbraid, wbraid, utm_*)
 * a vstupní stránku. S odesláním formuláře je pošleme do backendu spolu s GA
 * client_id a session_id; backend pak při změně stavu zakázky nahlásí Googlu,
 * jestli se z poptávky stala objednávka.
 *
 * Soukromí: v rámci návštěvy držíme údaje v sessionStorage (patří k poptávce,
 * kterou zákazník právě odesílá). Na 90 dní do localStorage je uložíme jen se
 * souhlasem s marketingovými cookies.
 */
import { consentState, hasMarketingConsent } from './consent';

const KEY = 'seknuto_attr_v1';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;
const PARAMS = ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const safe = (fn, fallback = null) => { try { return fn(); } catch { return fallback; } };

function readSession() {
  return safe(() => JSON.parse(sessionStorage.getItem(KEY))) || {};
}

function readPersistent() {
  const v = safe(() => JSON.parse(localStorage.getItem(KEY)));
  if (!v || !v.data || !v.expires || v.expires < Date.now()) return {};
  return v.data;
}

function writeSession(data) {
  safe(() => sessionStorage.setItem(KEY, JSON.stringify(data)));
}

function writePersistent(data) {
  safe(() => localStorage.setItem(KEY, JSON.stringify({ data, expires: Date.now() + TTL_MS })));
}

// Nové neprázdné hodnoty přepisují staré, prázdné nic nemažou
function merge(base, extra) {
  const out = { ...base };
  Object.entries(extra).forEach(([k, v]) => { if (v) out[k] = v; });
  return out;
}

export function captureAttribution(search = window.location.search, pathname = window.location.pathname) {
  const q = new URLSearchParams(search);
  const fromUrl = {};
  PARAMS.forEach((p) => {
    const v = (q.get(p) || '').trim().slice(0, 500);
    if (v) fromUrl[p] = v;
  });

  const session = readSession();
  const hasNewAd = Object.keys(fromUrl).length > 0;
  // Vstupní stránka: první stránka návštěvy, nebo stránka nového prokliku z reklamy
  if (hasNewAd || !session.landing_page) {
    fromUrl.landing_page = `${pathname}${search}`.slice(0, 500);
  }
  if (!Object.keys(fromUrl).length) return;

  const next = merge(session, fromUrl);
  writeSession(next);
  if (hasMarketingConsent()) writePersistent(merge(readPersistent(), next));
}

// GA4: _ga = "GA1.1.1234567890.1700000000" → client_id "1234567890.1700000000"
export function parseClientId(cookie) {
  const parts = String(cookie || '').split('.');
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : null;
}

// _ga_<ID> ve starém (GS1.1.1700000000.3…) i novém formátu (GS2.1.s1700000000$o3…)
export function parseSessionId(cookie) {
  const v = String(cookie || '');
  const gs2 = v.match(/^GS2\.\d+\.s(\d+)/);
  if (gs2) return gs2[1];
  const parts = v.split('.');
  return parts.length >= 3 && /^\d+$/.test(parts[2]) ? parts[2] : null;
}

function readCookie(name) {
  const m = safe(() => document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')}=([^;]*)`)));
  return m ? decodeURIComponent(m[1]) : null;
}

function gaIds() {
  const id = (process.env.REACT_APP_GA_ID || '').replace(/^G-/, '');
  return {
    ga_client_id: parseClientId(readCookie('_ga')),
    ga_session_id: id ? parseSessionId(readCookie(`_ga_${id}`)) : null,
  };
}

/** Co pošleme s formulářem. Jen neprázdné hodnoty, nic osobního. */
export function getLeadAttribution() {
  const data = merge(merge(readPersistent(), readSession()), gaIds());
  data.consent_state = consentState();
  const out = {};
  Object.entries(data).forEach(([k, v]) => { if (v) out[k] = v; });
  return out;
}

let listening = false;
export function initAttribution() {
  if (typeof window === 'undefined' || window.__PRERENDER__) return;
  captureAttribution();
  if (listening) return;
  listening = true;
  // Když zákazník souhlas s marketingem udělí až později, převedeme data
  // z této návštěvy do dlouhodobého úložiště.
  window.addEventListener('seknuto:consent', (e) => {
    if (e.detail && e.detail.marketing) writePersistent(merge(readPersistent(), readSession()));
  });
}
